import type { TRPCClientError } from '@trpc/client';
import type { FriendlyErrorResponse } from '@/errors/types';
import AuthError from '@/errors/AuthError';
import TokenExpiredError from '@/errors/TokenExpiredError';
import FatalServerError from '@/errors/FatalServerError';
import IdleError from '@/errors/IdleError';
import BetaRedirectionError from '@/errors/BetaRedirectionError';
import NetworkError from '@/errors/NetworkError';
import ServerError from '@/errors/ServerError';
import UserFriendlyError from '@/errors/UserFriendlyError';
import userFriendlyErrorFactory from '@/errors/UserFriendlyErrorFactory';
import LockedSectionError from '@/errors/LockedSectionError';
import ForcedFinishSectionError from '@/errors/ForcedFinishSectionError';
import { ErrorType } from '@/store/types';

/**
 * Matches tRPC error messages that embed legacy HTTP status info:
 * "[Legacy Status: 420 Code: E1612] Some error message"
 */
const LEGACY_MSG_REGEX = /^\[Legacy Status:\s*(\d+)\s+Code:\s*(E\d+)\]\s*(.*)$/;

/**
 * Build a synthetic FriendlyErrorResponse (AxiosError shape) from a TRPCClientError
 * so existing error-class constructors work unchanged.
 */
function toFriendlyErrorResponse(err: TRPCClientError<any>): FriendlyErrorResponse {
  let httpStatus: number = err.data?.httpStatus ?? 0;
  let message: string = err.data?.message ?? err.message;

  // Parse legacy status & error code embedded in the tRPC error message
  const legacyMatch = LEGACY_MSG_REGEX.exec(err.message);
  if (legacyMatch) {
    httpStatus = Number(legacyMatch[1]);
    const errorCode = legacyMatch[2];
    const errorMessage = legacyMatch[3];
    // Reconstruct message so UserFriendlyErrorFactory regex "(Error Code: E1234)" still matches
    message = `${errorMessage} (Error Code: ${errorCode})`;
  }

  const data = {
    errors: err.data?.errors ?? null,
    url: err.data?.url ?? '',
    message,
    errMessage: err.data?.errMessage,
    answers: err.data?.answers ?? [],
  };

  return {
    isAxiosError: true,
    name: 'AxiosError',
    message: err.message,
    config: {} as any,
    toJSON: () => ({}),
    response: {
      data,
      status: httpStatus,
      statusText: err.data?.code ?? 'TRPC_ERROR',
      headers: {},
      config: {} as any,
    },
  } as FriendlyErrorResponse;
}

/**
 * Map a TRPCClientError to the project's existing error classes
 * so ErrorBoundary.vue continues to work unchanged.
 */
export function mapTrpcError(err: TRPCClientError<any>): ServerError {
  // Network failure — no response data at all
  if (!err.data) {
    return new NetworkError(toFriendlyErrorResponse(err));
  }

  const code: string | undefined = err.data?.code;
  const errMessage: string = err.data?.errMessage ?? '';
  const friendly = toFriendlyErrorResponse(err);
  const httpStatus: number = friendly.response?.status ?? 0;
  const message: string = friendly.response?.data?.message ?? '';

  // TDM remote lock — must be distinguishable from a generic 403 so the caller
  // doesn't bounce the student between takeSection/loadSection forever.
  //
  // TODO(fragile-match): matching on the raw `message` string is the ONLY signal
  // the backend currently exposes to tell "locked"/"force-finished" apart from a
  // generic FORBIDDEN (e.g. "already completed", "not completed") — `code` is
  // 'FORBIDDEN' for all of them, and the JSON-RPC `error.code` (-32003) is just
  // the fixed numeric form of that same 'FORBIDDEN' string, so it doesn't help
  // either. If the backend ever rewords these messages, this match silently stops
  // working and the takeSection<->loadSection redirect loop bug comes back with
  // no compile-time or type-level warning.
  // Revisit by adding a stable machine-readable discriminator on the backend
  // (assessments.module.ts / assessments-review.module.ts), e.g. either:
  //   - populate `err.data.errMessage` with a stable code (e.g. 'SECTION_LOCKED')
  //     separate from the human-readable `message`, or
  //   - reuse the existing `addLegacyStatus(status, code, message)` + `E16xx`
  //     convention already used elsewhere in assessments.module.ts, extending
  //     `mapTrpcError`'s 420-only legacy-code path to also apply on 403s.
  // Then switch this check from `message === '...'` to the new field.
  if (code === 'FORBIDDEN' && message === 'Section is locked') {
    return new LockedSectionError(friendly, ErrorType.E1608);
  }

  // Proctor/teacher force-finish — same loop risk and same TODO as the lock above.
  if (code === 'FORBIDDEN' && message === 'Section is already force finished') {
    return new ForcedFinishSectionError(friendly, ErrorType.E1607);
  }

  // Auth errors
  if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN') {
    return new AuthError(friendly, message);
  }

  // Token expired — INTERNAL_SERVER_ERROR with specific errMessage
  if (code === 'INTERNAL_SERVER_ERROR' && errMessage === 'jwt expired') {
    return new TokenExpiredError(friendly);
  }

  // Idle / precondition
  if (httpStatus === 428 || code === 'PRECONDITION_FAILED') {
    return new IdleError(friendly);
  }

  // Beta redirection
  if (httpStatus === 418) {
    return new BetaRedirectionError(friendly);
  }

  // User-friendly error (420)
  if (httpStatus === 420) {
    return userFriendlyErrorFactory(friendly);
  }

  // Expected client errors — show toast, skip Rollbar
  if (code === 'NOT_FOUND' || code === 'BAD_REQUEST') {
    return new UserFriendlyError(friendly);
  }

  // Fatal server error (500+)
  if (code === 'INTERNAL_SERVER_ERROR' || httpStatus >= 500) {
    return new FatalServerError(friendly, friendly.response?.data?.message ?? '');
  }

  // Fallback
  return new ServerError(friendly);
}
